import { Mark } from 'charting_library';
import { TradeExecution } from '@/feature/terminal_tables/trade_history/TradeExecution';
import { Side } from '@/common/constants';

interface GroupedTradeMark {
  id: string;
  time: number;
  isBuy: boolean;
  isClose: boolean;
  totalSize: number;
  totalVolume: number;
  fillsCount: number;
  assetDecimals: number;
  quoteDecimals: number;
  assetCurrency: string;
}

export function calculateTradeMarks(filteredTrades: TradeExecution[], candleDurationSeconds: number, maxMarks: number): Mark[] {
  const groupedTrades = new Map<string, GroupedTradeMark>();

  filteredTrades.forEach((trade) => {
    const tradeTime = Math.floor(trade.dateTimestamp / 1000);
    const isBuy = (trade.side === Side.long) !== trade.isClose;

    let groupKey = trade.id;
    let groupTime = tradeTime;

    if (candleDurationSeconds > 0) {
      groupTime = Math.floor(tradeTime / candleDurationSeconds) * candleDurationSeconds;
      groupKey = `${groupTime}-${isBuy ? 'buy' : 'sell'}-${trade.isClose ? 'close' : 'open'}`;
    }

    if (!groupedTrades.has(groupKey)) {
      groupedTrades.set(groupKey, {
        id: groupKey,
        time: groupTime,
        isBuy,
        isClose: trade.isClose, // if a group mixes open/close, we assume the first one's status. For a side in a single candle, it's usually all close or all open.
        totalSize: trade.size,
        totalVolume: trade.size * trade.price,
        fillsCount: 1,
        assetDecimals: trade.assetDecimals,
        quoteDecimals: trade.quoteDecimals,
        assetCurrency: trade.assetCurrency,
      });
    } else {
      const group = groupedTrades.get(groupKey)!;
      group.totalSize += trade.size;
      group.totalVolume += trade.size * trade.price;
      group.fillsCount += 1;
    }
  });

  const groupedTradesArray = Array.from(groupedTrades.values());
  const cappedTrades = groupedTradesArray.length > maxMarks ? groupedTradesArray.slice(0, maxMarks) : groupedTradesArray;

  return cappedTrades.map((group, index) => {
    const buyOrSell = group.isBuy ? 'Buy' : 'Sell';
    const actionText = group.isClose ? `${buyOrSell} to Close` : buyOrSell;
    const avgPrice = group.totalSize > 0 ? group.totalVolume / group.totalSize : 0;

    let text = `${actionText} ${Number(group.totalSize.toFixed(group.assetDecimals))} ${group.assetCurrency} @ $${Number(avgPrice.toFixed(group.quoteDecimals))}`;

    if (group.fillsCount > 1) {
      text += ` (${group.fillsCount} fills)`;
    }

    return {
      id: group.id || index.toString(),
      time: group.time,
      color: group.isBuy ? 'green' : 'red', // todo use MarkCustomColor
      text: text,
      label: group.isBuy ? 'B' : 'S',
      labelFontColor: 'white',
      minSize: 15,
    };
  });
}
